import {SoundFile} from "../../components/types";

// --- Drag type constants ---

export const CUE_ROW_DRAG_TYPE = "application/x-soundspots-cue-row";
export const AMBIENT_ROW_DRAG_TYPE = "application/x-soundspots-ambient-row";

// --- Drag data detection ---

export const hasDroppedFiles = (dataTransfer?: DataTransfer | null) => {
    if (!dataTransfer) {
        return false;
    }
    if (dataTransfer.files && dataTransfer.files.length > 0) {
        return true;
    }
    return Array.from(dataTransfer.types || []).includes("Files");
};

export const getCueRowDragId = (dataTransfer?: DataTransfer | null): string | undefined => {
    if (!dataTransfer) {
        return undefined;
    }
    const types = Array.from(dataTransfer.types || []);
    if (!types.includes(CUE_ROW_DRAG_TYPE)) {
        return undefined;
    }
    const value = dataTransfer.getData(CUE_ROW_DRAG_TYPE);
    return value || undefined;
};

export const getAmbientRowDragId = (dataTransfer?: DataTransfer | null): string | undefined => {
    if (!dataTransfer) {
        return undefined;
    }
    const types = Array.from(dataTransfer.types || []);
    if (!types.includes(AMBIENT_ROW_DRAG_TYPE)) {
        return undefined;
    }
    const value = dataTransfer.getData(AMBIENT_ROW_DRAG_TYPE);
    return value || undefined;
};

// --- Shared drag-drop helpers ---

export type DragSource =
    | {kind: "cueRow"; id: string}
    | {kind: "ambientRow"; id: string}
    | {kind: "soundLibrary"}
    | {kind: "files"}
    | {kind: "unknown"};

export const classifyDragSource = (
    dataTransfer: DataTransfer | null | undefined,
    draggingCueId: string | undefined,
    draggingAmbientZoneId: string | undefined,
    hasSoundLibraryDragType: (dt: DataTransfer) => boolean
): DragSource => {
    const cueId = draggingCueId || getCueRowDragId(dataTransfer);
    if (cueId) return {kind: "cueRow", id: cueId};

    const ambientId = draggingAmbientZoneId || getAmbientRowDragId(dataTransfer);
    if (ambientId) return {kind: "ambientRow", id: ambientId};

    if (dataTransfer && hasSoundLibraryDragType(dataTransfer)) return {kind: "soundLibrary"};
    if (hasDroppedFiles(dataTransfer)) return {kind: "files"};

    return {kind: "unknown"};
};

export const computeReorderMarkerPosition = (
    sourceIndex: number,
    hoveredIndex: number
): "top" | "bottom" => {
    return (sourceIndex >= 0 && hoveredIndex > sourceIndex) ? "bottom" : "top";
};

export type ConfirmFn = (options: {
    title: string;
    body: string;
    confirmText: string;
    isDestructive: boolean;
}) => Promise<boolean>;

export const confirmSoundReplacement = async (
    confirm: ConfirmFn,
    entityLabel: string,
    newFileName: string,
    isAmbient: boolean
): Promise<boolean> => {
    return confirm({
        title: isAmbient ? "Replace ambient sound" : "Replace sound file",
        body: `${entityLabel} already has a sound file. Replace it with "${newFileName}"?`,
        confirmText: "Replace",
        isDestructive: true
    });
};

export const hasExistingSound = (sound?: SoundFile | null): boolean => {
    return !!(sound?.fileName || sound?.originalFileName);
};
